import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { alpha, Badge, Box, ButtonBase, Divider, Drawer, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { globalHeaderHeightNormal, globalHeaderHeightSmall } from '../GlobalHeader/constants';
import { useCommonWebTranslation, useHoverSupported, useSmallDevice } from '../hooks';
import { themeColors, themeMode } from '../JuvareTheme';
import { globalMenuWidthFull, globalMenuWidthSmall } from './constants';
/**
 * Renders the global sidebar menu with configurable menu items, badges, tooltips, and interaction handlers.
 * Supports toggling between icon-only and icon-with-label display modes, responsive behavior on small/mobile devices,
 * and scrollable/sticky layout with top and bottom sections.
 *
 * @param {GlobalMenuProps} props - Props for configuring the global menu.
 * @param {boolean} props.menuOpen - Whether the menu is visible.
 * @param {'expand-on-hover' | 'icon' | 'icon-and-label'} [props.mode] - Current display mode of the menu.
 * @param {GlobalMenuItem[]} props.menuItems - Main navigation menu items.
 * @param {GlobalMenuItem[]} [props.bottomMenuItems] - Optional footer menu items.
 * @param {string | number} [props.selectedMenuItemId] - ID of the currently selected menu item.
 * @param {(item: GlobalMenuItem) => boolean} [props.isMenuItemSelected] - Optional custom selection check.
 * @param {(item: GlobalMenuItem) => void} [props.onSelectMenuItem] - Callback when a menu item is selected.
 * @param {boolean} [props.hideHeader] - Whether the global header is hidden.
 * @param {(mode: 'icon' | 'icon-and-label') => void} props.setMenuMode - Callback to toggle the menu display mode.
 *
 * @returns {JSX.Element} The rendered sidebar menu component.
 */
export const GlobalMenu = (props) => {
    const { t } = useCommonWebTranslation();
    const smallDevice = useSmallDevice();
    const isMobile = !useHoverSupported();
    const headerHeight = !props.hideHeader ? (smallDevice ? globalHeaderHeightSmall : globalHeaderHeightNormal) : 0;
    const createBottomMenu = () => {
        if (props.bottomMenuItems && props.bottomMenuItems.length > 0) {
            return (React.createElement(Box, { sx: { overflowX: 'hidden', alignSelf: 'flex-end' } },
                React.createElement(Divider, null),
                createMenuButtons(props.bottomMenuItems)));
        }
        else {
            return React.createElement("div", null);
        }
    };
    const isFull = () => {
        return props.mode ? props.mode == 'icon-and-label' : false;
    };
    const createMenuButtons = (menuItems) => {
        return menuItems.map((item) => {
            var _a;
            const selected = props.selectedMenuItemId != null
                ? item.id === props.selectedMenuItemId
                : props.isMenuItemSelected
                    ? props.isMenuItemSelected(item)
                    : false;
            const icon = selected ? ((_a = item.iconSelected) !== null && _a !== void 0 ? _a : item.icon) : item.icon;
            const handleMenuItemAriaLabel = (menuItem) => {
                if (menuItem.badge != null) {
                    if (typeof menuItem.badge === 'number') {
                        return t('common.badge', { value: menuItem.label, count: menuItem.badge });
                    }
                    return t('common.badgeForCustomCount', { value: menuItem.label, customCount: menuItem.badge });
                }
                else {
                    return menuItem.label;
                }
            };
            const itemDisabled = item.disabled !== undefined && typeof item.disabled === 'function' ? item.disabled() : item.disabled;
            const itemHidden = item.hidden !== undefined && typeof item.hidden === 'function' ? item.hidden() : item.hidden;
            return (React.createElement(Tooltip, { key: item.label, title: props.mode !== 'icon-and-label' ? (item.tooltip || item.label) + (item.badge ? ` (${item.badge})` : '') : undefined, placement: 'right', arrow: true },
                React.createElement(ButtonBase, { focusRipple: true, TouchRippleProps: { style: { marginTop: 4, marginBottom: 4, marginLeft: 8, marginRight: 8 } }, sx: {
                        pl: 1,
                        pr: 1,
                        pt: 0.5,
                        pb: 0.5,
                        display: itemHidden ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 2,
                        '&:hover>div': {
                            backgroundColor: selected ? themeColors.primary.surface : alpha(themeColors.icon.subtle, 0.05),
                        },
                        opacity: itemDisabled ? 0.4 : 1,
                    }, href: item.href, onClick: (event) => {
                        if (item.onClick || props.onSelectMenuItem) {
                            event.preventDefault();
                        }
                        if (item.onClick) {
                            item.onClick();
                        }
                        if (props.onSelectMenuItem) {
                            props.onSelectMenuItem(item);
                        }
                    }, "data-test": item['data-test'], "aria-label": handleMenuItemAriaLabel(item), disabled: itemDisabled, hidden: itemHidden },
                    React.createElement(Box, { sx: {
                            boxSizing: 'border-box',
                            width: '100%',
                            height: 48,
                            paddingLeft: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            color: selected ? themeColors.primary.base : themeColors.icon.subtle,
                            backgroundColor: selected ? themeColors.primary.surface : undefined,
                            borderRadius: 1,
                            overflow: 'hidden',
                        } },
                        item.badge != null ? (React.createElement(Badge, { color: 'secondary', variant: isFull() && props.mode !== 'icon' ? 'standard' : 'dot', sx: { '& .MuiBadge-dot': { backgroundColor: themeColors.accent.red.bold } } }, icon)) : (icon),
                        props.mode !== 'icon' && (React.createElement(Typography, { sx: { ml: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, item.label)),
                        item.badge != null && props.mode !== 'icon' && (React.createElement(React.Fragment, null,
                            React.createElement(Typography, { variant: 'caption', sx: {
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxSizing: 'border-box',
                                    minWidth: '20px',
                                    height: '20px',
                                    marginLeft: '4px',
                                    padding: '0px 6px',
                                    borderRadius: '10px',
                                    lineHeight: 1,
                                    color: themeColors.text.inverse,
                                    backgroundColor: themeColors.accent.red.bold,
                                }, "aria-hidden": 'true' }, item.badge)))))));
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Drawer, { variant: isMobile ? 'temporary' : 'permanent', open: props.menuOpen, onClose: props.onCloseMenu, ModalProps: {
                keepMounted: true,
                sx: {
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                },
            }, slotProps: {
                paper: {
                    sx: Object.assign(Object.assign({ position: 'fixed' }, (isMobile
                        ? {}
                        : {
                            visibility: props.menuOpen ? 'visible' : 'hidden',
                            left: props.menuOpen ? 0 : isFull() ? -globalMenuWidthFull : -globalMenuWidthSmall,
                        })), { top: headerHeight, height: `calc(100% - ${headerHeight}px)`, width: isFull() && props.mode !== 'icon' ? globalMenuWidthFull : globalMenuWidthSmall, pt: 1, pb: isMobile ? 1 : 0, display: 'grid', bgcolor: themeColors.overlay.primary, boxShadow: (theme) => theme.shadows[2], transition: (theme) => theme.transitions.create('left') +
                            ', ' +
                            theme.transitions.create('width') +
                            ', ' +
                            theme.transitions.create('visibility'), zIndex: (theme) => theme.zIndex.appBar - 1, overflowX: 'hidden', gridTemplateRows: isMobile ? 'auto max-content min-content' : 'auto max-content min-content min-content', '@media print': {
                            display: 'none',
                        } }),
                },
            } },
            React.createElement(Box, { sx: Object.assign({ overflow: 'hidden', overflowY: 'auto' }, {
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                        width: 10,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 0, 0, 0.2)',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(0, 0, 0, 0.3)',
                    },
                }) },
                createMenuButtons(props.menuItems),
                React.createElement(Box, { sx: {
                        position: 'sticky',
                        bottom: 0,
                        height: 40,
                        background: themeMode === 'light'
                            ? 'linear-gradient(to bottom,rgba(255,255,255,0),rgba(255,255,255,0.42),rgba(255,255,255,0.72),rgba(255,255,255,0.8),rgb(255,255,255,1))'
                            : 'linear-gradient(to bottom,rgba(33,39,43,0),rgba(33,39,43,0.42),rgba(33,39,43,0.72),rgba(33,39,43,0.8),rgb(33,39,43,1))',
                    } })),
            createBottomMenu(),
            React.createElement(MenuToggleSection, { mode: props.mode, onToggle: () => {
                    const newDisplayMode = props.mode == 'icon' || !props.mode ? 'icon-and-label' : 'icon';
                    props.setMenuMode(newDisplayMode);
                } }))));
};
const MenuToggleSection = (props) => {
    const { t } = useCommonWebTranslation();
    const hoverSupported = useHoverSupported();
    const mobileMenu = !hoverSupported;
    if (mobileMenu) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Divider, { sx: { marginTop: 'auto' } }),
        React.createElement(Tooltip, { title: t('globalMenu.toggleMenu') },
            React.createElement(ButtonBase, { focusRipple: true, TouchRippleProps: { style: { marginTop: 4, marginBottom: 4, marginLeft: 8, marginRight: 8 } }, sx: {
                    pl: 1,
                    pr: 1,
                    pt: 0.5,
                    pb: 0.5,
                    ml: 'auto',
                    width: globalMenuWidthSmall,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }, onClick: props.onToggle, "aria-expanded": props.mode == 'icon-and-label', "data-test": 'menu-toggle-button' },
                React.createElement(Box, { sx: {
                        boxSizing: 'border-box',
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        color: themeColors.icon.subtle,
                        borderRadius: 1,
                    } }, props.mode == 'icon-and-label' ? React.createElement(KeyboardDoubleArrowLeftIcon, null) : React.createElement(KeyboardDoubleArrowRightIcon, null))))));
};
